import { Box, Grid } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  AppointmentChangeStatus,
  AppointmentStatusChangeModal,
  APPOINTMENT_CHANGE_STATUS,
} from "@/components/appointment-status-change";
import { FlexCenterWrapper } from "../../../../components/DefaultNoDataWrapper";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import { PdfGenerator } from "../../../../components/PdfGenerator";
import PdfViewDrawer from "../../../../components/PdfViewDrawer";
import SuccessModal, { useSuccess } from "../../../../components/SuccessModal";
import Text from "../../../../components/Text";
import PaymentDrawer from "../../../../components/app/provider/billing/payment/PaymentDrawer";
import { PATIENT_PATHS } from "../../../../constants/paths";
// import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useLatestReports } from "../../../../queries/patient-portal/dashboard/use-latest-reports";
import { useEncounterSummeryV2 } from "../../../../queries/encounter/use-encounter-summery-v2";
import { usePatientRequiringAction } from "../../../../queries/patient-portal/dashboard/use-patient-requiring-action";
import { usePaymentsDue } from "../../../../queries/patient-portal/dashboard/use-payments-due";
import { useAppointmentsV2 } from "../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { getStripePromise } from "../../../../queries/stripe/use-stripe";
import { useVisitHistory } from "../../../../queries/patient-portal/use-visit-history";
import EncounterSummeryTemplate, {
  useResolveEncounterLogo,
} from "../../../../template/EncounterTemplate";
import { getStartEndDateOfSameDay } from "../../../../utils/date-time-utility";
import { DEF_PAGINATION, getXtenantId } from "../../../../utils/utils";
import PatientPortalRescheduleAppointmentModal from "../appointment/patientAppointmentForm/PatientPortalRescheduleAppointmentModal";
import DashboardAppointmentCard from "./components/AppointmentCard";
import DashboardActionsRequired from "./components/DashboardActionsRequired";
import PaymentCard from "./components/PaymentCard";
import ReportItem from "./components/ReportItem";
import VisitHistoryItem from "./components/VisitHistoryItem";
import ScrollableContainer from "./components/shared/ScrollableContainer";
import SectionCard from "./components/shared/SectionCard";
import SectionHeader from "./components/shared/SectionHeader";
import {
  Appointment,
  Payment,
  Report,
  Visit,
} from "./patient-portal-dashboard.types";

type SectionType = "appointments" | "visits" | "payments" | "reports";

const SectionTabs: Record<SectionType, string> = {
  appointments: PATIENT_PATHS.APPOINTMENTS,
  visits: PATIENT_PATHS.VISITS,
  payments: PATIENT_PATHS.PAYMENTS,
  reports: PATIENT_PATHS.REPORTS,
} as const;
const currentDate = dayjs().startOf("day").toISOString();
const endDateOfYear = dayjs().endOf("year").toISOString();

const { startTime, endTime } = getStartEndDateOfSameDay(new Date());

const PatientPortalDashBoard: React.FC = () => {
  const navigate = useNavigate();
  // const [patient] = useCurrentUser();
  const { patientId } = useOutletContext<{ patientId: any }>();
  const { data: appointments, isFetching } = useAppointmentsV2({
    pagination: DEF_PAGINATION,
    startDate: currentDate,
    endDate: endDateOfYear,
    appointmentStatus: ["PENDING", "CONFIRMED", "CHECKED_IN"],
    patientUuid: [patientId ?? ""],
    providerGroupId: getXtenantId(),
    disabled: !patientId,
  });

  const { data: requiringAction } = usePatientRequiringAction({
    patient: patientId ?? "",
    pagination: DEF_PAGINATION,
    startDate: startTime ?? undefined,
    endDate: endTime ?? undefined,
    status: "PENDING",
  });

  const { data: paymentsDueData, isFetching: isFetchingPaymentsDue } =
    usePaymentsDue({
      patientUuid: patientId ?? "",
      pagination: DEF_PAGINATION,
    });
  const paymentsDueList: Payment[] = paymentsDueData?.data?.content ?? [];

  // Dashboard card shows up to 5 latest reports (BE default size: 5)
  const { data: latestReportsData, isFetching: isFetchingLatestReports } =
    useLatestReports({
      patientUuid: patientId ?? "",
      pagination: { ...DEF_PAGINATION, pageSize: 5 },
    });
  const latestReportsList: Report[] = latestReportsData?.data?.content ?? [];

  const { data: visitHistory, isFetching: isFetchingVisitHistory } =
    useVisitHistory({
      patientUuid: patientId ?? "",
      page: 0,
      size: 20,
      sortBy: "start_time",
      sortDirection: "desc",
      disabled: !patientId,
    });

  const visitHistoryList: Visit[] = visitHistory?.data?.content ?? [];

  const requiringActionList = requiringAction?.data?.content ?? [];
  const requiringActionCount = requiringActionList.length;

  const [
    openedSuccessModal,
    { open: openSuccessModal, close: closeSucessModal },
  ] = useSuccess();
  const [appointmentStatusChangeDisclosure, appointmentDisclosure] =
    useReduceDisclosure(2);
  const [statusChangedTo, _setStatusChangedTo] =
    useState<AppointmentChangeStatus>(APPOINTMENT_CHANGE_STATUS.CANCELLED);
  const [selectedAppointment, setSelectedAppointment] = useState<
    Appointment | undefined
  >();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [selectedRescheduleAppointment, setSelectedRescheduleAppointment] =
    useState<any>();
  const rescheduleDisclosure = useDisclosure(false);
  const payDisc = useDisclosure(false);
  const client = useQueryClient();

  const upcomingAppointments = appointments?.data?.content ?? [];

  const handleViewAll = (section: SectionType) => {
    navigate(`${SectionTabs[section]}`);
  };

  const handleCancel = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    appointmentStatusChangeDisclosure.open();
  };

  const handleReschedule = (appointmentUuid: string) => {
    const appointment = upcomingAppointments?.find(
      (item) => item?.uuid === appointmentUuid
    );
    setSelectedRescheduleAppointment(appointment);
    rescheduleDisclosure.open();
  };

  const handlePayNow = (payment: Payment) => {
    payDisc.open({
      uuid: null,
      patientId,
      superBillId: payment.superBillUuid,
      amount: payment.amountDue,
    });
  };

  const handleViewReport = (report: Report) => {
    if (report.labDocument) {
      window.open(report.labDocument, "_blank", "noopener,noreferrer");
    }
  };

  // ─── PDF / Encounter Summary State ───────────────────────────────────────────
  const [selectedVisitId, setSelectedVisitId] = useState<string | undefined>();
  const printViewDisclosure = useDisclosure(false, {
    onClose: () => setSelectedVisitId(undefined),
  });
  const [previewFile, setPreviewFile] = useState<string>();
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const pdfRef = useRef<{ generatePdf: (name?: string) => void }>(null);

  const { data: encounterPrefillData, isLoading: isEncounterPrefillLoading } =
    useEncounterSummeryV2({
      appointmentUuid: selectedVisitId!,
      xTENANTID: getXtenantId(),
    });

  const resolvedEncounterData = useResolveEncounterLogo(
    encounterPrefillData?.data
  );

  useEffect(() => {
    if (selectedVisitId && resolvedEncounterData) {
      setIsSummaryLoading(true);
      pdfRef.current?.generatePdf();
    }
  }, [resolvedEncounterData]);
  // ─────────────────────────────────────────────────────────────────────────────

  const handleViewVisitSummary = (visitUuid: string) => {
    setSelectedVisitId(visitUuid);
  };

  const handleViewVisitDetails = (isUpcoming: boolean) => {
    navigate(
      `${PATIENT_PATHS.APPOINTMENTS}?appointment=${isUpcoming ? "upcoming" : "past"}`
    );
  };

  return (
    <Box
      width={"100%"}
      sx={{
        height: "calc(100svh - 40px)",
        overflow: "hidden",
        p: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <AppointmentStatusChangeModal
        disclosureHost={appointmentStatusChangeDisclosure}
        appointmentId={selectedAppointment?.uuid}
        status={statusChangedTo}
        onSuccess={(message) => {
          appointmentDisclosure.close();
          setSuccessMessage(message);
          openSuccessModal();
        }}
      />

      <SuccessModal
        label={successMessage}
        opened={openedSuccessModal}
        onClose={closeSucessModal}
      />
      <PatientPortalRescheduleAppointmentModal
        open={rescheduleDisclosure.opened}
        onClose={rescheduleDisclosure.close}
        appointmentData={selectedRescheduleAppointment}
      />

      <Elements stripe={getStripePromise()}>
        <PaymentDrawer
          disclosureHost={payDisc}
          amount={payDisc?.data?.amount}
          shouldPassPatientId
          onSuccess={() => {
            client.invalidateQueries({ queryKey: ["payments-due"] });
          }}
        />
      </Elements>

      <PdfGenerator
        ref={pdfRef}
        onSave={(blob) => {
          setIsSummaryLoading(false);
          if (!blob) return;
          const url = window.URL.createObjectURL(blob);
          setPreviewFile(url);
          printViewDisclosure.open();
        }}
      >
        <EncounterSummeryTemplate data={resolvedEncounterData} />
      </PdfGenerator>

      <PdfViewDrawer
        w="65vw"
        file={previewFile}
        disclosureHost={printViewDisclosure}
        title="Visit Summary"
      />

      <LoadingOverlay
        isLoading={isEncounterPrefillLoading || isSummaryLoading}
      />

      <Grid container spacing={2} sx={{ height: "100%" }}>
        {/* Upcoming Appointments */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", minHeight: { md: "50%" } }}
        >
          <SectionCard>
            <SectionHeader
              title="Upcoming Appointments"
              onViewAll={() => handleViewAll("appointments")}
            />
            <ScrollableContainer>
              {isFetching && (
                <FlexCenterWrapper>
                  <Text>Loading...</Text>
                </FlexCenterWrapper>
              )}

              {!isFetching &&
              upcomingAppointments &&
              upcomingAppointments?.length <= 0 ? (
                <FlexCenterWrapper>
                  <Text>No Upcoming Appointments found</Text>
                </FlexCenterWrapper>
              ) : (
                upcomingAppointments?.map((appointment) => (
                  <DashboardAppointmentCard
                    key={appointment.uuid}
                    appointment={appointment}
                    actions={{
                      onCancel: handleCancel,
                      onReschedule: handleReschedule,
                      onJoinVisit: navigate,
                    }}
                  />
                ))
              )}
            </ScrollableContainer>
          </SectionCard>
        </Grid>

        {/* Visit History */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", minHeight: { md: "50%" } }}
        >
          <SectionCard>
            <SectionHeader
              title="Visit History"
              onViewAll={() => handleViewAll("visits")}
            />
            <ScrollableContainer>
              {isFetchingVisitHistory && (
                <FlexCenterWrapper>
                  <Text>Loading...</Text>
                </FlexCenterWrapper>
              )}

              {!isFetchingVisitHistory && visitHistoryList.length <= 0 ? (
                <FlexCenterWrapper>
                  <Text>No appointments available.</Text>
                </FlexCenterWrapper>
              ) : (
                visitHistoryList.map((visit) => (
                  <VisitHistoryItem
                    key={visit.uuid}
                    visit={visit}
                    actions={{
                      onViewSummary: handleViewVisitSummary,
                      onViewDetails: handleViewVisitDetails,
                    }}
                  />
                ))
              )}
            </ScrollableContainer>
          </SectionCard>
        </Grid>

        {/* Payments Due */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", minHeight: { md: "50%" } }}
        >
          <SectionCard>
            <SectionHeader
              title="Payments Due"
              onViewAll={() => handleViewAll("payments")}
            />
            <ScrollableContainer>
              {isFetchingPaymentsDue && (
                <FlexCenterWrapper>
                  <Text>Loading...</Text>
                </FlexCenterWrapper>
              )}

              {!isFetchingPaymentsDue && paymentsDueList.length === 0 ? (
                <FlexCenterWrapper>
                  <Text>No Payments Due found</Text>
                </FlexCenterWrapper>
              ) : (
                paymentsDueList.map((payment) => (
                  <PaymentCard
                    key={payment.superBillUuid}
                    payment={payment}
                    actions={{ onPayNow: handlePayNow }}
                  />
                ))
              )}
            </ScrollableContainer>
          </SectionCard>
        </Grid>

        {/* Latest Reports */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <SectionCard>
            <SectionHeader
              title="Latest Reports"
              onViewAll={() => handleViewAll("reports")}
            />
            <ScrollableContainer>
              {isFetchingLatestReports && (
                <FlexCenterWrapper>
                  <Text>Loading...</Text>
                </FlexCenterWrapper>
              )}

              {!isFetchingLatestReports && latestReportsList.length === 0 ? (
                <FlexCenterWrapper>
                  <Text>No reports available.</Text>
                </FlexCenterWrapper>
              ) : (
                latestReportsList.map((report) => (
                  <ReportItem
                    key={report.uuid}
                    report={report}
                    actions={{ onView: handleViewReport }}
                  />
                ))
              )}
            </ScrollableContainer>
          </SectionCard>
        </Grid>

        {/* Actions Required */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <SectionCard>
            <SectionHeader
              title="Actions Required"
              count={requiringActionCount}
            />
            <DashboardActionsRequired actions={requiringActionList} />
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientPortalDashBoard;
